import { useEffect, useRef } from 'react'
import { apiClient } from '../api/client'
import { useAuth } from '../auth/auth-context'
import { combinedItemCount, useCartCount } from './cart-count-context'

/**
 * Module-level guard: at most one hydrate `GET /cart` per AUTHENTICATED
 * session, even across multiple mounts of the component that calls this hook
 * (e.g. `<AppShell>` staying mounted across every route change is the normal
 * case, but this guard is robust even if it were ever unmounted and
 * remounted within the same browser session). Reset when auth transitions
 * away from `authenticated` (see the `prevStatusRef` tracking inside
 * `useHydrateCartCount`) so a later authenticated mount — including a SOFT
 * re-login/refresh that never reloads the page/module, e.g. after
 * `logout()` — re-hydrates instead of leaving the badge stuck on the prior
 * session's count.
 */
let hasHydratedThisSession = false

/**
 * Test-only escape hatch — vitest reuses this module's state across every
 * `it()` within one test FILE (module registries are isolated per file, not
 * per test), so a test file that needs a fresh hydrate for each test must
 * call this in `beforeEach`.
 */
export function resetCartCountHydration() {
  hasHydratedThisSession = false
}

/**
 * UID-423 (ADR-0127 §H) — reverses `cart-count-context.tsx`'s earlier
 * decision to never fire a global `GET /cart` on app mount. Meant to be
 * called exactly once, from `<AppShell>` (mounted inside both
 * `<AuthProvider>` and `<CartCountProvider>` — see `App.tsx`), so it has
 * both `useAuth()`'s status and `useCartCount()`'s setter available.
 *
 * Fires at most one `GET /cart` per session, and ONLY once the session is
 * confirmed `authenticated` — never while `loading`, `unauthenticated`, or
 * `onboarding_required`. Since `<AppShell>` itself is only ever routed to
 * for authenticated app surfaces (`/login`, `/signup`, and onboarding all
 * render OUTSIDE it — see `App.tsx`), and this hook additionally gates on
 * `auth.status === 'authenticated'`, it never fires on those routes even if
 * that routing boundary ever moved.
 *
 * A 401 is handled globally (`authMiddleware` flips `auth.status`) — nothing
 * extra to do here. Any OTHER failure (network error, 5xx, a malformed
 * response) leaves the badge untouched (`lineCount` stays whatever it was,
 * including `undefined`/"not yet known") rather than zeroing it — the badge
 * self-heals the next time the buyer visits the Cart or any add-to-cart path
 * pushes a fresh count. The one-attempt-per-session guard applies even on
 * failure: this does not retry within the session.
 */
export function useHydrateCartCount() {
  const auth = useAuth()
  const { setLineCount } = useCartCount()
  // Per-instance guard, so a re-render with an unchanged `auth.status` never
  // re-triggers the effect body's async work even before the module flag
  // above is set (the module flag alone would still be correct, but this
  // avoids a same-tick double-fire race between two mounted instances).
  const attemptedRef = useRef(false)
  // Tracks the PREVIOUS `auth.status` across renders, so the effect below
  // can detect an authenticated -> not-authenticated transition (a logout,
  // or any other soft de-auth) and reset the once-per-session guards. Without
  // this, `logout()` — a SOFT state change (`setUnauthenticated()`, no page
  // reload: see `auth-context.tsx`'s `logout`) — would leave
  // `hasHydratedThisSession` permanently `true`, so a subsequent soft
  // re-login within the same page load would never re-hydrate and the badge
  // could keep showing the PRIOR session's count. Real login doesn't need
  // this (`startLogin` is a hard `window.location.href` redirect that
  // reloads the module, zeroing the flag naturally) but this guard is
  // correct and harmless for that path too.
  const prevStatusRef = useRef(auth.status)

  useEffect(() => {
    const prevStatus = prevStatusRef.current
    prevStatusRef.current = auth.status

    if (prevStatus === 'authenticated' && auth.status !== 'authenticated') {
      hasHydratedThisSession = false
      attemptedRef.current = false
    }

    if (auth.status !== 'authenticated') return
    if (hasHydratedThisSession || attemptedRef.current) return
    attemptedRef.current = true
    hasHydratedThisSession = true

    let cancelled = false
    void (async () => {
      try {
        // A 401 is handled globally (authMiddleware flips auth.status); any
        // other error/missing-data case intentionally falls through to
        // "leave the badge untouched" — see this function's doc comment —
        // so `error` itself is never read here.
        const { data } = await apiClient.GET('/cart')
        if (cancelled) return
        if (data) {
          // UID-423 (ADR-0127 §H): COMBINED count (part/BOM lines + requested
          // services), matching every other push site.
          setLineCount(combinedItemCount(data))
        }
      } catch {
        // Network/parse error — leave the badge untouched (see doc comment).
      }
    })()

    return () => {
      cancelled = true
    }
  }, [auth.status, setLineCount])
}
